<template>
  <div class="box-chat position-relative">
    <div class="position-absolute w-100 h-100 d-flex flex-column">
      <div class="box-body flex-grow-1 overflow-auto word-break-normal" ref="messageContainer">
        <infinite-loading direction="top" @infinite="loadMessages" spinner="waveDots">
          <template slot="no-more">
            <h5><span class="badge badge-secondary">Beginning of the conversation</span></h5>
          </template>
          <template slot="no-results">
            <h5><span class="badge badge-secondary">There are no messages yet</span></h5>
          </template>
          <template slot="error" slot-scope="{ trigger }">
            <h5><span class="badge badge-danger">Whoops. It looks like the server is unavailable.</span>
            </h5>
            <div class="text-center">
              <el-button size="mini" @click="trigger()">Retry</el-button>
            </div>
          </template>
        </infinite-loading>
        <component
            v-for="message in messages"
            :key="message.id"
            :message="message"
            :contact="contact"
            :outgoing-number="outgoingNumber"
            :brand="brand"
            :settings="getMessageSettings"
            :email-message-settings="getEmailMessageSettings"
            :email-address="getEmailAddress"
            v-bind:is="getMessageComponentName(message)"
        >
        </component>
      </div>
      <div class="wrapper w-100 schedule__link" v-if="havePendingMessage">
        <a class="cursor-pointer" @click="showScheduleList"><i class="far fa-clock"></i> See all scheduled messages</a>
      </div>
      <send-box
          ref="sendBox"
          :brand="brand"
          @outbound-message-created="refresh"
          class="flex-shrink-0 flex-grow-0 mt-2"
          :types="types"
          :conversation="conversation"
          :styles="styles"
      ></send-box>
    </div>
    <media-dialog></media-dialog>
    <attachment-dialog :index-media-url="indexMediaUrl" :store-media-url="storeMediaUrl" :can-create-media="canCreateMedia"></attachment-dialog>
    <attachment-form></attachment-form>
    <edit-message-form @stored="updateMessage"></edit-message-form>
    <dialog-edit-contact :conversation-id="conversation.id"></dialog-edit-contact>
    <schedule-dialog :pending-message-index-url="getPendingMessageIndexUrl"
        :store-pending-message-url="getStorePendingMessageUrl"
        @stored="onStoredScheduleMessage"
    ></schedule-dialog>
    <original-email-message />
  </div>
</template>

<script>
import InboundTextMessage from "./messages/inbound-text-message";
import OutboundTextMessage from "./messages/outbound-text-message";
import InboundCallMessage from "./messages/inbound-call-message";
import OutboundCallMessage from "./messages/outbound-call-message";
import SendBox from "./send-box";
import AutoHeight from "../auto-height";
import {addQueryParams} from "../../libs/url";
import MediaDialog from "./media-dialog";
import eventBus from "../../event-bus";
import EditMessageForm from "./edit-message-form";
import AttachmentDialog from "../attachment/attachment-dialog";
import AttachmentForm from "../attachment/attachment-form";
import DialogEditContact from "../../components/contact/dialog-edit-contact";
import ScheduleDialog from "./schedule-message/schedule-dialog";
import InboundMailMessage from "./messages/inbound-mail-message";
import OutboundMailMessage from "./messages/outbound-mail-message";
import OriginalEmailMessage from "./original-email-message";

const messageTypeMap = {
  1: 'outbound-text-message',
  2: 'inbound-text-message',
  3: 'outbound-call-message',
  4: 'inbound-call-message',
  5: 'outbound-mail-message',
  6: 'inbound-mail-message',
};

export default {
  components: {
    AttachmentDialog,
    AttachmentForm,
    'outbound-text-message': OutboundTextMessage,
    'inbound-text-message': InboundTextMessage,
    'outbound-call-message': OutboundCallMessage,
    'inbound-call-message': InboundCallMessage,
    'inbound-mail-message': InboundMailMessage,
    'outbound-mail-message': OutboundMailMessage,
    SendBox,
    AutoHeight,
    MediaDialog,
    EditMessageForm,
    DialogEditContact,
    ScheduleDialog,
    OriginalEmailMessage,
  },

  props: {
    indexUrl: String,
    conversation: Object,
    adaptiveRefresh: {
      type: Boolean,
      default: false,
    },
    autoRefresh: {
      type: Boolean,
      default: true,
    },
    types: Object,
    styles: Object,
    indexMediaUrl: String,
    storeMediaUrl: String,
    canCreateMedia: Boolean,
  },

  data: function () {
    return {
      messages: [],
      nextUrl: null,
      lastUpdate: null,
      cycleActive: false,
      refreshInterval: 10000,
      wrapWidth: null,
      timerId: null,
      isRefreshing: false,
      excludeMessages: [],
      chatMode: true,
    };
  },

  computed: {
    /**
     * @return {Object|null}
     */
    contact() {
      return _.get(this, 'conversation.contact', null);
    },

    /**
     * @return {Object|null}
     */
    outgoingNumber() {
      return _.get(this, 'conversation.outgoing_number', null);
    },

    /**
     * @return {Object|null}
     */
    brand() {
      return _.get(this, 'conversation.outgoing_number.brand', null);
    },

    /**
     * @return {String}
     */
    updateUrl() {
      return addQueryParams(this.indexUrl, {timestamp: this.lastUpdate, excluded: this.getExcludeMessages});
    },

    /**
     * @return {Array}
     */
    getMessageSettings() {
      return _.get(this.conversation, 'messageSettings');
    },

    /**
     * @return {Array}
     */
    getExcludeMessages() {
      return this.excludeMessages;
    },

    /**
     * @return {String}
     */
    getStorePendingMessageUrl() {
      return _.get(this.conversation, 'storePendingMessageUrl');
    },

    /**
     * @return {String}
     */
    getPendingMessageIndexUrl() {
      return _.get(this.conversation, 'pendingMessageIndexUrl');
    },

    /**
     * @return {Boolean}
     */
    havePendingMessage() {
      return !_.isEmpty(_.get(this.conversation, 'pending_messages'));
    },

    /**
     * @return {Object}
     */
    getEmailAddress() {
      return _.get(this.conversation, 'email_conversation.email_address');
    },

    /**
     * @return {Object}
     */
    getEmailMessageSettings() {
      return _.get(this.conversation, 'email_conversation.messageSettings');
    },
  },

  methods: {
    /**
     * @param {Object} $state
     */
    loadMessages($state) {
      if (!this.nextUrl) {
        return $state.complete();
      }

      axios.get(this.nextUrl)
          .then(({data}) => {
            this.handleChunk(data.data || []);

            if (!this.lastUpdate) {
              this.lastUpdate = data.timestamp;
            }

            this.nextUrl = _.get(data, 'links.next');

            $state.loaded();

            if (!this.cycleActive) {
              this.cycleActive = true;
              this.startAutoRefresh();
            }
          })
          .catch(error => $state.error());
    },

    /**
     * @param {Array} items
     */
    handleChunk(items) {
      _.each(items, item => {
        const index = _.findIndex(this.messages, {id: item.id});
        if (index >= 0) {
          this.replaceMessage(item, index);
        } else {
          this.prependMessage(item);
        }
      });
    },

    /**
     * @return {undefined}
     */
    startAutoRefresh() {
      this.timerId = _.delay(function autoRefresh() {
        const start = performance.now();
        this.refresh().then(() => {
          const delay = this.getNextRefreshDelay(start, performance.now());
          this.timerId = _.delay(autoRefresh.bind(this), delay);
        });
      }.bind(this), this.refreshInterval);
    },

    /**
     * @return {undefined}
     */
    refresh() {
      if (this.isRefreshing) {
        return;
      }
      this.isRefreshing = true;
      return axios.get(this.updateUrl)
          .then(({data}) => {
            const hasNewMessages = this.handleModifiedMessages(data.data || []);
            this.lastUpdate = data.timestamp;
            if (hasNewMessages) {
              this.scrollToBottom('instant');
            }
          })
          .catch(error => {
            //
          })
          .finally(() => this.isRefreshing = false);
    },

    /**
     * @param {Array} messages
     * @return {Boolean}
     */
    handleModifiedMessages(messages) {
      let newMessageCount = 0;
      _.each(messages, message => {
        const index = _.findIndex(this.messages, {id: message.id});
        if (index >= 0) {
          return this.replaceMessage(message, index);
        }
        if (this.isNewMessage(message)) {
          this.pushMessage(message);
          newMessageCount++;
        }
      });

      return newMessageCount > 0;
    },

    /**
     * @param {Object} message
     */
    isNewMessage(message) {
      if (_.isEmpty(this.messages)) {
        return true;
      }

      return message.created_at.timestamp >= _.last(this.messages).created_at.timestamp;
    },

    /**
     * @param {Object} message
     */
    prependMessage(message) {
      this.messages.splice(0, 0, message);
    },

    /**
     * @param {Object} message
     */
    pushMessage(message) {
      this.messages.push(message);
    },

    /**
     * @param {Object} message
     * @param {Number} index
     */
    replaceMessage(message, index) {
      this.messages.splice(index, 1, message);
    },

    /**
     * @param {Object} message
     */
    updateMessage(message) {
      const index = _.findIndex(this.messages, {id: message.id});

      this.messages.splice(index, 1, message);
    },

    /**
     * @param {String} smooth
     */
    scrollToBottom(smooth = 'smooth') {
      const el = this.$refs.messageContainer;
      this.$nextTick(function () {
        if (typeof el.scroll === "function") {
          el.scroll({
            top: el.scrollHeight,
            behavior: smooth ? 'smooth' : 'instant'
          });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    /**
     * @param {Number} start
     * @param {Number} end
     * @return {Number}
     */
    getNextRefreshDelay(start, end) {
      const k = this.refreshInterval / 500;
      if (this.adaptiveRefresh) {
        const value = _.ceil((end - start) * k);
        return value > this.refreshInterval ? value : this.refreshInterval;
      }

      return this.refreshInterval;
    },

    /**
     * @return {String}
     */
    getMessageComponentName(message) {
      return _.get(messageTypeMap, message.type);
    },

    /**
     * @param {Object} message
     * @emits on-mark-unread
     */
    markUnread({message}) {
      const id = _.get(message, 'id');
      const existed = _.find(this.getExcludeMessages, function (item) {
        return item === id;
      });

      if (_.isUndefined(existed)) {
        this.excludeMessages.push(id);
        eventBus.$emit('on-mark-unread', this.conversation.id);
      }
    },

    /**
     * @event show-schedule-dialog
     * @return {undefined}
     */
    showScheduleList() {
      eventBus.$emit('show-schedule-dialog', {
        component: 'schedule-list',
        conversationId: _.get(this.conversation, 'id')
      });
    },

    /**
     * @return {undefined}
     */
    onStoredScheduleMessage() {
      this.$refs.sendBox.clear();
    },
  },

  /**
   * @return {undefined}
   */
  mounted() {
    this.nextUrl = this.indexUrl;
    if (!this.autoRefresh) {
      this.cycleActive = true;
    }
  },

  /**
   * @return {undefined}
   */
  created() {
    eventBus.$on('on-message-updated', this.updateMessage);
    eventBus.$on('on-message-mark-unread', this.markUnread);
  },

  /**
   * @return {undefined}
   */
  beforeDestroy() {
    this.timerId !== null && clearTimeout(this.timerId);
    eventBus.$off('on-message-updated', this.updateMessage);
    eventBus.$off('on-message-mark-unread', this.markUnread);
  },
}
</script>
